<template>
    <div>
        <Hero ref="hero" title="" lead="" class="jobs-hero absolute top-0 left-0 w-full z-0" />

        <div
            ref="container"
            class="container relative z-10"
            :style="$device.isMobileOrTablet ? 'min-height: calc(100vh + 50px)' : ''"
        >
            <div ref="heroContent" class="pt-24 lg:pt-32">
                <h1 class="aux-text mt-4 sm:mt-8 lg:mt-16 mb-4">{{ $t('jobs.title') }}</h1>

                <p class="title title-xl 4xl pr-16 sm:pr-0">
                    {{ $t('job_seekers.lead') }}
                </p>
                <Portal v-if="$device.isMobileOrTablet" selector="#__layout__content">
                    <JobFilters
                        :filters="tempFilters"
                        :options="options"
                        :close-mobile-filter-panel="closeMobileFilterPanel"
                        :set-filter="setFilter___mobile"
                        :reset-filters="resetFilters___mobile"
                        :class="$store.state.jobs.isMobileFilterPanelOverlappingHeader ? 'z-45' : 'z-20'"
                    />
                </Portal>

                <JobFilters
                    v-else
                    :filters="activeFilters"
                    :options="options"
                    :set-filter="setFilter___desktop"
                    :reset-filters="resetFilters___desktop"
                    class="pb-4 mt-24 pt-2.5"
                />
            </div>

            <Portal selector="#__layout__content">
                <div
                    v-if="$device.isMobileOrTablet"
                    class="fixed bottom-0 left-0 w-full purple-gradient-bg transition-transform"
                    :class="[
                        $store.state.jobs.isMobileFilterPanelOverlappingHeader ? 'z-50' : 'z-25',
                        !$store.state.jobs.isMobileFilterCTAContainerVisible ? 'translate-y-full' : '',
                    ]"
                >
                    <div class="container py-5 flex justify-between items-center">
                        <Button
                            v-if="!$store.state.jobs.isMobileFilterPanelOpen"
                            :label="$t('jobs.search.submit')"
                            class="p-button-secondary text-center flex justify-center mx-auto transition-all"
                            icon="pi pi-search"
                            icon-pos="left"
                            @click="
                                $store.commit(
                                    'jobs/setIsMobileFilterPanelOpen',
                                    !$store.state.jobs.isMobileFilterPanelOpen
                                )
                            "
                        />
                        <Button
                            v-if="$store.state.jobs.isMobileFilterPanelOpen"
                            :label="$t('generic.cancel')"
                            class="p-button-secondary text-center flex justify-center mx-auto transition-all"
                            @click="cancelFilters___mobile"
                        />
                        <Button
                            v-if="$store.state.jobs.isMobileFilterPanelOpen"
                            :label="$t('jobs.search.submit')"
                            class="p-button-primary text-center flex justify-center mx-auto transition-all"
                            icon="pi pi-search"
                            icon-pos="left"
                            @click="applyFilters___mobile"
                        />
                    </div>
                </div>
            </Portal>

            <div id="jobs-list" class="mt-14 md:mt-24">
                <Pagination :pages-count="pagesCount" :page="$route.query.page ? +$route.query.page : 1" class="mb-8" />

                <!--            <component :is="'style'" type="text/css">-->
                <!--                [data-id="{{ uniqueId }}"] div.bar[data-div-id="item.id"]::before {-->
                <!--                content: "{{ item.content }}";-->
                <!--                color: {{ item.color }};-->
                <!--                }-->
                <!--            </component>-->

                <div class="job-cards-grid">
                    <JobCard
                        v-for="(job, i) in jobs"
                        :key="job.id"
                        :index="i"
                        :title="job.title"
                        :location="
                            (job.regions.length ? job.regions.map((x) => x.title).join(', ') : '') +
                            (job.country && job.regions.length ? ', ' : '') +
                            (job.country ? job.country.name : '')
                        "
                        :url="localePath(`/job-seeker/jobs/${job.slug}`)"
                        :employment-type="job.employment_type.name"
                        :sector="job.sector ? job.sector.name : null"
                        :deadline="job.deadline ? $dayjs(job.deadline * 1000).format('YYYY. MM. DD') : null"
                        :is-featured="job.is_highlighted"
                    />
                </div>
            </div>

            <Pagination
                :pages-count="pagesCount"
                scroll-target="#jobs-list"
                :page="$route.query.page ? +$route.query.page : 1"
                class="my-8"
            />

            <div v-if="!jobs.length" :class="$device.isMobileOrTablet ? 'pt-12 xs:pt-24 lg:pt-32' : ''">
                <p class="title title-lg">
                    <span v-if="Object.keys($route.query).length">
                        {{ $t('jobs.no_results') }}
                    </span>
                    <span v-else>{{ $t('generic.no_results') }}</span>
                </p>
            </div>
        </div>
    </div>
</template>

<script>
import { throttle } from 'throttle-debounce';
import Hero from '@/components/Heroes/Hero';
import JobCard from '~/components/Cards/JobCard/JobCard';
import JobFilters from '@/components/Generic/JobFilters';
import Pagination from '@/components/UI/Pagination';

export default {
    name: 'Index',
    components: {
        Hero,
        JobCard,
        JobFilters,
        Pagination,
    },
    async asyncData({ $axios, route, error, i18n, redirect, localePath, $getIsLangKoZhDe }) {
        if ($getIsLangKoZhDe()) {
            return redirect(localePath('/'));
        }

        let _jobs = null;
        let _jobsCount = null;
        let _pagesCount = null;
        let _options = {};
        let _notfound = false;

        const activeFilters = {
            country: null,
            sector: null,
            region: null,
            employment_type: null,
            position: null,
            language: null,
        };

        const query = { ...route.query };

        // remove props with no value, otherwise they will be stringified as '?param=null',
        // causing the search results to not appear
        Object.keys(query).forEach((key) => {
            if (activeFilters[key] === 'undefined') {
                delete query[key];
            }
        });

        const ITEMS_PER_PAGE = 24;

        const offset = route.query.page ? (+route.query.page - 1) * ITEMS_PER_PAGE : '';

        try {
            await $axios
                .$get(`/job?limit=${ITEMS_PER_PAGE}&offset=${offset}&${new URLSearchParams(query).toString()}`)
                .then((res) => {
                    if (res.success === 1) {
                        if (res.offset > res.total) {
                            _notfound = true;
                        }

                        _jobs = res.data;
                        _jobsCount = res.total;
                        _options = { ...res.filters };
                        _pagesCount = Math.ceil(res.total / ITEMS_PER_PAGE);
                    }
                })
                .catch((err) => {
                    console.log(err);
                });
        } catch (err) {
            console.log(err);
        }

        if (_notfound) {
            return error({ statusCode: 404, message: i18n.t('error.page_not_found.title') });
        }

        Object.keys(activeFilters).forEach((key) => {
            if (route.query[key]) {
                const filterValueIDs = route.query[key].split(',');
                activeFilters[key] = _options[key].reduce((acc, curr) => {
                    if (filterValueIDs.includes(String(curr.id))) {
                        acc.push(curr);
                    }
                    return acc;
                }, []);
            }
        });

        // Temporary filters are used only on mobile.
        // Since on mobile a filter select change won't trigger a filter query param change right away, as on desktop,
        // tempFilters is used to hold the search params until the 'search' button is pressed.
        const tempFilters = { ...activeFilters };

        return {
            jobs: _jobs,
            jobsCount: _jobsCount,
            options: _options,
            pagesCount: _pagesCount,
            activeFilters,
            tempFilters,
        };
    },
    data() {
        return {
            // save nothing here, use the vuex store instead on this page
            // (data is lost when setting a filter)
            scrollHandler: throttle(100, false, this.handleScroll),
            resizeHandler: throttle(100, false, this.handleResize),
        };
    },
    head() {
        return {
            title: this.$t('jobs.title'),
            meta: [
                {
                    hid: 'og:title',
                    property: 'og:title',
                    content: this.$getOgTitle(this.$t('jobs.title')),
                },
                {
                    hid: 'title',
                    name: 'title',
                    content: this.$getOgTitle(this.$t('jobs.title')),
                },
            ],
        };
    },
    watchQuery: true,
    mounted() {
        window.addEventListener('scroll', this.scrollHandler);
        window.addEventListener('resize', this.resizeHandler);
        this.$store.commit('jobs/setLastSearchQuery', this.$route.query);

        setTimeout(() => {
            this.updateHeroHeight();
            this.$store.commit('cookie/setJobOffersLastCheckedDate', this.$dayjs().format('YYYY-MM-DD%20HH:mm:ss'));
        }, 100);
    },
    beforeDestroy() {
        window.removeEventListener('scroll', this.scrollHandler);
        window.removeEventListener('resize', this.resizeHandler);
    },
    methods: {
        setFilter___desktop(key, value) {
            this.activeFilters[key] = value;

            const query = { ...this.$route.query };

            // reset page to 1 after filtering to avoid 404 errors
            query.page = '1';

            if (!value || !value.length) {
                delete query[key];
            } else {
                query[key] = value.map((x) => x.id).join(',');
            }

            this.$nextTick(() => {
                this.updateHeroHeight();
            });

            this.$store.commit('jobs/setLastSearchQuery', query);

            this.$router.replace({ path: this.$route.path, query });
        },
        setFilter___mobile(key, value) {
            this.tempFilters[key] = value;
        },
        resetFilters___desktop() {
            Object.keys(this.activeFilters).forEach((key) => {
                this.activeFilters[key] = null;
            });

            this.$store.commit('jobs/setLastSearchQuery', {});

            this.$nextTick(() => {
                this.updateHeroHeight();
            });

            this.$router.replace({ path: this.$route.path, query: {} });
        },
        resetFilters___mobile() {
            Object.keys(this.tempFilters).forEach((key) => {
                this.tempFilters[key] = null;
            });
        },
        applyFilters___mobile() {
            this.activeFilters = { ...this.tempFilters };
            const query = { ...this.activeFilters };
            // reset page to 1 after filtering to avoid 404 errors
            Object.keys(query).forEach((key) => {
                const value = query[key];

                if (!value || !value.length) {
                    delete query[key];
                } else {
                    query[key] = value.map((x) => x.id).join(',');
                }
            });

            this.$store.commit('jobs/setIsMobileFilterPanelOpen', false);
            this.$store.commit('jobs/setLastSearchQuery', query);

            if (JSON.stringify(this.$store.state.jobs.lastSearchQuery) !== JSON.stringify(this.$route.query)) {
                this.$router.replace({
                    path: this.$route.path,
                    query: this.$store.state.jobs.lastSearchQuery,
                });
            }
        },
        cancelFilters___mobile() {
            this.tempFilters = { ...this.activeFilters };

            this.$store.commit('jobs/setIsMobileFilterPanelOpen', !this.$store.state.jobs.isMobileFilterPanelOpen);
        },
        updateHeroHeight() {
            this.$refs.hero.$el.style.height = this.$refs.heroContent.getBoundingClientRect().height + 100 + 'px';
        },
        closeMobileFilterPanel() {
            this.$store.commit('jobs/setIsMobileFilterPanelOpen', false);
        },
        manageMobileFiltersCTAContainerVisibility() {
            if (!this.$refs.container) {
                return;
            }

            if (window.scrollY + window.innerHeight > this.$refs.container.scrollHeight) {
                if (this.$store.state.jobs.isMobileFilterCTAContainerVisible) {
                    this.$store.commit('jobs/setIsMobileFilterCTAContainerVisible', false);
                }
            } else if (!this.$store.state.jobs.isMobileFilterCTAContainerVisible) {
                this.$store.commit('jobs/setIsMobileFilterCTAContainerVisible', true);
            }
        },
        handleScroll() {
            this.manageMobileFiltersCTAContainerVisibility();
        },
        handleResize() {
            this.updateHeroHeight();
            this.manageMobileFiltersCTAContainerVisibility();
        },
    },
};
</script>

<style>
/* These styles are only used to cover up the ~300ms delay on page load until the job hero height is set in js */

.desktop .jobs-hero,
.desktop .jobs-hero > div {
    @apply min-h-[374px] md:min-h-[784px] lg:min-h-[642px] xl:min-h-[552px];
}

body:not(.desktop) {
    .jobs-hero,
    .jobs-hero > div {
        @apply min-h-[350px] md:min-h-[350px] lg:min-h-[450px] xl:min-h-[450px];
    }
}
</style>
